import { LABELS } from '@mm/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { Badge, Card, EmptyState } from '../../components/ui/display';
import { DataTable } from '../../tables/DataTable';
import type { AccountApps } from '../../types/models';
import { formatDate } from '../../utils/format';
import { durationLabel } from './duration';

type Row = AccountApps['history'][number];

/** Account page: apps using the account now, and every assignment period. */
export function AccountAppsCard({
  resource,
  accountId,
}: {
  resource: 'advertising-accounts' | 'revenue-accounts';
  accountId: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['account-apps', resource, accountId],
    queryFn: () => api.get<AccountApps>(`/${resource}/${accountId}/apps`),
  });

  return (
    <Card
      title="Apps using this account"
      description="Assign or remove from the app’s page. Removed periods stay in the history."
      bodyClassName="p-0"
    >
      <DataTable<Row>
        tableId="account-apps"
        rows={data?.history}
        loading={isLoading}
        rowKey={(r) => r.id}
        columns={[
          {
            key: 'app',
            header: 'App',
            render: (r) => (
              <Link
                to={`/apps/${r.app.id}`}
                className="flex items-center gap-2 hover:text-brand-700"
              >
                <AppIcon src={r.app.iconUrl} name={r.app.name} size={28} />
                <span>
                  <span className="block font-medium text-slate-900">{r.app.name}</span>
                  {r.app.developerName && (
                    <span className="block text-xs text-slate-500">{r.app.developerName}</span>
                  )}
                </span>
              </Link>
            ),
          },
          { key: 'assignedOn', header: 'Added', render: (r) => formatDate(r.assignedOn) },
          {
            key: 'removedOn',
            header: 'Removed',
            render: (r) => (r.removedOn ? formatDate(r.removedOn) : '—'),
          },
          {
            key: 'duration',
            header: 'Duration',
            align: 'right',
            render: (r) => durationLabel(r.durationDays),
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => (
              <Badge tone={r.status === 'ACTIVE' ? 'green' : 'slate'}>
                {LABELS.assignmentStatus[r.status]}
              </Badge>
            ),
          },
        ]}
        empty={<EmptyState title="Not assigned to any app yet" />}
      />
    </Card>
  );
}
